import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { API } from "@/App";
import axios from "axios";
import { toast } from "sonner";
import {
  Search,
  Globe,
  CheckCircle2,
  AlertTriangle,
  RefreshCw,
  ArrowLeft,
  ExternalLink,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";

const GSCSiteSelectPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const tempToken = searchParams.get("token");
  
  const [loading, setLoading] = useState(true);
  const [sites, setSites] = useState([]);
  const [tenantId, setTenantId] = useState(null);
  const [selectedSite, setSelectedSite] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchSites = async () => {
      if (!tempToken) {
        setError("Missing authentication token");
        setLoading(false);
        return;
      }

      try {
        const response = await axios.get(`${API}/auth/gsc/sites`, {
          params: { temp_token: tempToken }
        });
        
        setSites(response.data.sites || []);
        setTenantId(response.data.tenant_id);
        
        if (response.data.sites?.length === 0) {
          setError("No verified sites found in your Google Search Console account");
        }
      } catch (err) {
        console.error("Error fetching GSC sites:", err);
        setError(err.response?.data?.detail || "Failed to load sites. Please try again.");
      } finally {
        setLoading(false);
      }
    };

    fetchSites();
  }, [tempToken]);

  const handleSelectSite = async () => {
    if (!selectedSite || !tempToken) return;
    
    setSaving(true);
    try {
      await axios.post(`${API}/auth/gsc/complete`, {
        temp_token: tempToken,
        site_url: selectedSite
      });
      
      toast.success("Google Search Console connected successfully!");
      navigate("/admin?tab=integrations");
    } catch (err) {
      console.error("Error completing GSC setup:", err);
      toast.error(err.response?.data?.detail || "Failed to complete setup");
    } finally {
      setSaving(false);
    }
  };

  const getSiteDisplayName = (siteUrl) => {
    // Handle domain property format
    if (siteUrl.startsWith("sc-domain:")) {
      return siteUrl.replace("sc-domain:", "");
    }
    // Handle URL property format
    try {
      const url = new URL(siteUrl);
      return url.hostname;
    } catch {
      return siteUrl;
    }
  };

  const getSiteType = (siteUrl) => {
    if (siteUrl.startsWith("sc-domain:")) {
      return "Domain Property";
    }
    return "URL Property";
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-orange-50 flex items-center justify-center">
        <div className="text-center">
          <RefreshCw className="w-8 h-8 animate-spin text-orange-600 mx-auto mb-4" />
          <p className="text-slate-600">Loading your Search Console sites...</p>
        </div>
      </div>
    );
  }

  if (error && sites.length === 0) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-orange-50 flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="pt-6 text-center">
            <AlertTriangle className="w-12 h-12 text-amber-500 mx-auto mb-4" />
            <h2 className="text-lg font-semibold mb-2">No Sites Found</h2>
            <p className="text-slate-600 mb-4">{error}</p>
            <div className="space-y-2">
              <Button 
                variant="outline" 
                onClick={() => navigate("/admin?tab=integrations")}
                className="w-full gap-2"
              >
                <ArrowLeft className="w-4 h-4" />
                Back to Integrations
              </Button>
              <a 
                href="https://search.google.com/search-console" 
                target="_blank" 
                rel="noopener noreferrer"
                className="block"
              >
                <Button variant="link" className="w-full gap-2 text-orange-600">
                  <ExternalLink className="w-4 h-4" />
                  Go to Google Search Console
                </Button>
              </a>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-orange-50 py-12 px-4">
      <div className="max-w-2xl mx-auto">
        <Card>
          <CardHeader className="text-center">
            <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Search className="w-8 h-8 text-orange-600" />
            </div>
            <CardTitle className="text-2xl">Select Your Website</CardTitle>
            <CardDescription>
              Choose which website to connect to Google Search Console tracking
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {sites.map((site, idx) => {
              const siteUrl = site.siteUrl || site;
              const isSelected = selectedSite === siteUrl;
              
              return (
                <button
                  key={idx}
                  onClick={() => setSelectedSite(siteUrl)}
                  className={`w-full p-4 rounded-lg border-2 transition-all text-left flex items-center gap-4 ${
                    isSelected 
                      ? "border-orange-500 bg-orange-50" 
                      : "border-slate-200 hover:border-orange-300 bg-white"
                  }`}
                  data-testid={`gsc-site-${idx}`}
                >
                  <div className={`p-2 rounded-lg ${isSelected ? "bg-orange-100" : "bg-slate-100"}`}>
                    <Globe className={`w-5 h-5 ${isSelected ? "text-orange-600" : "text-slate-500"}`} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-slate-900 truncate">
                      {getSiteDisplayName(siteUrl)}
                    </p>
                    <p className="text-sm text-slate-500 truncate">{siteUrl}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge variant="outline" className="text-xs">
                      {getSiteType(siteUrl)}
                    </Badge>
                    {isSelected && (
                      <CheckCircle2 className="w-5 h-5 text-orange-600" />
                    )}
                  </div>
                </button>
              );
            })}

            <div className="pt-4 flex gap-3">
              <Button 
                variant="outline" 
                onClick={() => navigate("/admin?tab=integrations")}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button 
                onClick={handleSelectSite}
                disabled={!selectedSite || saving}
                className="flex-1 bg-orange-600 hover:bg-orange-700"
              >
                {saving ? (
                  <>
                    <RefreshCw className="w-4 h-4 animate-spin mr-2" />
                    Connecting...
                  </>
                ) : (
                  "Connect Site"
                )}
              </Button>
            </div>

            <p className="text-xs text-center text-slate-500 pt-2">
              Don&apos;t see your site? Make sure it&apos;s verified in{" "}
              <a 
                href="https://search.google.com/search-console" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-orange-600 hover:underline"
              >
                Google Search Console
              </a>
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default GSCSiteSelectPage;
